/**
 * CAN THIS COUNTER RECEIVE A NEW ORDER RIGHT NOW, AND IF NOT, WHY - one answer
 * per counter, derived once (PRD BAK-063).
 *
 * The counter used to be told three of the reasons a shop stops trading - its
 * own pause, the platform's unattended pause and the kill switch - and none of
 * the others. On a weekday the shop never opens, on a recorded holiday, outside
 * its service hours, with its preparation capacity full or with its bank
 * details unverified, allocation sent it nothing while its own board looked
 * exactly like an open morning with no orders in it. A baker reading "Aucune
 * commande" on a closed Monday cannot tell a quiet shop from a shut one.
 *
 * So the whole question is answered here, and the device answers none of it:
 *
 *   open         the fact. Green or grey on the board, and nothing else.
 *   reason       ONE reason, already chosen by the precedence below - the
 *                device picks the words for it and never ranks two.
 *   reopensAt    when it lifts BY ITSELF: the end of a timed pause, the next
 *                opening, the next hour with room. Null when nothing lifts it
 *                on a clock - a person has to.
 *   closedThrough  the last day of a recorded closure run, for "fermee
 *                jusqu'au 20 septembre inclus". Null otherwise, and null when
 *                the run reaches past the days the caller supplied, because a
 *                last day nobody looked at is not a last day.
 *   actions      what the counter may do about it, the same idea as the order
 *                payload's `actions`: RESUME only while a pause exists, so the
 *                control is never drawn over a kill switch it cannot lift.
 *
 * PRECEDENCE IS THE TUPLE'S ORDER, and it runs from what only the platform can
 * lift to what time lifts by itself. The two suspensions sit above all of it
 * and are not here at all: every `/b` call answers them with a 403
 * (`org_suspended`, `location_suspended`), so no context ever carries one.
 *
 * WHAT IT READS IS WHAT ALLOCATION READS. The day's services and the recorded
 * closures are the facts the candidate snapshot turns into `WEEKLY_CLOSURE`
 * and `EXCEPTIONAL_CLOSURE`, the pause is the one `pauses.ts` turns into
 * `ORDERS_PAUSED`, the capacity intervals are the ones the window picker
 * refuses a full hour on, the payout verdict is the one
 * `COMMERCIALLY_INELIGIBLE` is, and the pickup readiness is the one
 * `PICKUP_INCOMPLETE` is (`pickupReadinessOf`, BAK-035). `apps/api-worker/test/bakery-intake.test.ts`
 * holds the two sides to each other.
 *
 * Pure and I/O-free, over instants the caller has already resolved in the
 * counter's own zone: this package has no time-zone arithmetic and must not
 * grow a second copy of `routing`'s.
 */

import type { ServicePauseReason } from '../enums';
import { type CounterReadiness, isCounterDispatchable } from './counter-readiness';

/**
 * Every reason, in PRECEDENCE order - the first that holds is the one sent.
 * `OPEN` is the absence of all of them and is first only by convention.
 */
export const COUNTER_INTAKE_REASONS = [
  'OPEN',
  /** OPS-009: the kill switch reached this counter. Internal lifts it. */
  'PLATFORM_SWITCH',
  /** The house's bank details are not verified, so allocation skips it. */
  'PAYOUT_UNVERIFIED',
  /**
   * BAK-035: the shop lacks what a courier is booked on - a contact mobile, an
   * address or coordinates - so allocation skips it (`PICKUP_INCOMPLETE`).
   * Beside the payout because both say the shop is not SET UP, and an
   * operator, not the baker or a clock, completes it (founder, 2026-10-07).
   */
  'PICKUP_INCOMPLETE',
  /** BAK-038: two orders in a row went unanswered. The counter lifts it. */
  'UNATTENDED_EXPIRED',
  /**
   * BAK-038's retired silence rule. No pause reader returns it any more
   * (`getPause` ignores a stored one), so the platform never sends it; it is
   * kept because the wire's `ServicePauseReason` still names it and a reason
   * the pause can carry must have a word.
   */
  'UNATTENDED_HEARTBEAT',
  /** BAK-006: the baker's own pause, timed or until resumed. */
  'PAUSED',
  /** BAK-010/BAK-047: a closure recorded for today. */
  'CLOSURE_DAYS',
  /** BAK-027: a weekday this counter never trades on. */
  'WEEKLY_CLOSURE',
  /** BAK-046: today is a trading day and none of its services is running. */
  'OUTSIDE_HOURS',
  /** BAK-011: the hour the counter is in has no pickup left. */
  'CAPACITY_FULL',
] as const;
export type CounterIntakeReason = (typeof COUNTER_INTAKE_REASONS)[number];

export const COUNTER_INTAKE_ACTIONS = ['PAUSE', 'RESUME'] as const;
export type CounterIntakeAction = (typeof COUNTER_INTAKE_ACTIONS)[number];

/** One service of one day, resolved to instants (epoch milliseconds). */
export interface CounterIntakeService {
  readonly opensAt: number;
  /**
   * When it stops. A service with no configured end is resolved by the CALLER
   * to the end of its day: this function does not guess where a day ends.
   */
  readonly closesAt: number;
}

export interface CounterIntakeDay {
  readonly serviceDay: string;
  /** A closure is recorded for this day (BAK-010, BAK-047). */
  readonly closed: boolean;
  /** Its services in the counter's zone; empty on a weekday it never trades. */
  readonly services: readonly CounterIntakeService[];
}

/** One hourly capacity interval (BAK-011), as the rate model states it. */
export interface CounterIntakeCapacity {
  readonly start: number;
  readonly end: number;
  readonly remainingPickups: number;
}

export interface CounterIntakeInput {
  readonly now: number;
  readonly servicePaused: boolean;
  readonly payoutVerified: boolean;
  /** `pickupReadinessOf(shop).ready` - the answer allocation reads. */
  readonly pickupReady: boolean;
  readonly pause: { readonly until: number | null; readonly reason: ServicePauseReason } | null;
  /**
   * Today FIRST, then consecutive days. Past the last one nothing is known, so
   * a next opening beyond it is reported as unknown (null), never invented.
   */
  readonly days: readonly CounterIntakeDay[];
  /** Today's intervals; null when this counter does not track capacity. */
  readonly capacity: readonly CounterIntakeCapacity[] | null;
}

export interface CounterIntake {
  readonly open: boolean;
  readonly reason: CounterIntakeReason;
  readonly reopensAt: string | null;
  readonly closedThrough: string | null;
  readonly actions: readonly CounterIntakeAction[];
}

/** The first service opening strictly after `after`, skipping closed days. */
function nextOpening(days: readonly CounterIntakeDay[], after: number): number | null {
  for (const day of days) {
    if (day.closed) continue;
    let soonest: number | null = null;
    for (const service of day.services) {
      if (service.opensAt > after && (soonest === null || service.opensAt < soonest)) {
        soonest = service.opensAt;
      }
    }
    if (soonest !== null) return soonest;
  }
  return null;
}

/**
 * The next instant inside one of today's services whose hour still has room,
 * after the full hour `now` is in - or tomorrow's first opening when today has
 * none left.
 */
function nextRoom(input: CounterIntakeInput, today: CounterIntakeDay): number | null {
  let soonest: number | null = null;
  for (const service of today.services) {
    if (service.closesAt <= input.now) continue;
    for (const interval of input.capacity ?? []) {
      if (interval.remainingPickups <= 0) continue;
      if (interval.start <= input.now) continue;
      if (interval.end <= service.opensAt || interval.start >= service.closesAt) continue;
      const at = Math.max(interval.start, service.opensAt);
      if (soonest === null || at < soonest) soonest = at;
    }
  }
  return soonest ?? nextOpening(input.days.slice(1), input.now);
}

export function counterIntakeOf(input: CounterIntakeInput): CounterIntake {
  const actions: readonly CounterIntakeAction[] = input.pause === null ? ['PAUSE'] : ['RESUME'];
  const shut = (
    reason: Exclude<CounterIntakeReason, 'OPEN'>,
    reopensAt: number | null,
    closedThrough: string | null = null,
  ): CounterIntake => ({
    open: false,
    reason,
    reopensAt: reopensAt === null ? null : new Date(reopensAt).toISOString(),
    closedThrough,
    actions,
  });

  if (input.servicePaused) return shut('PLATFORM_SWITCH', null);
  if (!input.payoutVerified) return shut('PAYOUT_UNVERIFIED', null);
  if (!input.pickupReady) return shut('PICKUP_INCOMPLETE', null);
  if (input.pause !== null) {
    switch (input.pause.reason) {
      case 'UNATTENDED_EXPIRED':
        return shut('UNATTENDED_EXPIRED', null);
      case 'UNATTENDED_HEARTBEAT':
        return shut('UNATTENDED_HEARTBEAT', null);
      case 'MANUAL':
        return shut('PAUSED', input.pause.until);
    }
  }

  const today = input.days[0];
  if (today !== undefined) {
    if (today.closed) {
      let last = 0;
      while (input.days[last + 1]?.closed === true) last += 1;
      const reachesPastHorizon = last === input.days.length - 1;
      return shut(
        'CLOSURE_DAYS',
        nextOpening(input.days, input.now),
        reachesPastHorizon ? null : (input.days[last]?.serviceDay ?? null),
      );
    }
    if (today.services.length === 0) {
      return shut('WEEKLY_CLOSURE', nextOpening(input.days, input.now));
    }
    const running = today.services.find(
      (service) => service.opensAt <= input.now && input.now < service.closesAt,
    );
    if (running === undefined) {
      return shut('OUTSIDE_HOURS', nextOpening(input.days, input.now));
    }
    const hour = input.capacity?.find(
      (interval) => interval.start <= input.now && input.now < interval.end,
    );
    if (hour !== undefined && hour.remainingPickups <= 0) {
      return shut('CAPACITY_FULL', nextRoom(input, today));
    }
  }

  return { open: true, reason: 'OPEN', reopensAt: null, closedThrough: null, actions };
}

/**
 * The Active bakeries table's one word about a counter (OPS-035), in the
 * order its filter offers them.
 */
export const BAKERY_SERVICE_STATUSES = [
  'OPEN',
  'UNREACHABLE',
  'CLOSED',
  'VACATION',
  'PAUSED',
  'IGNORING_ORDERS',
  'INCOMPLETE',
  'SUSPENDED',
] as const;
export type BakeryServiceStatus = (typeof BAKERY_SERVICE_STATUSES)[number];

/**
 * WHY the word is Paused or Suspended, where the word alone does not say
 * (#2004, approved 2026-10-08): the text beside the status pill. A pause is a
 * pause whoever made it - the baker, the kill switch, or two orders nobody
 * answered - and a house that cannot be paid is Suspended because its payout
 * setup is unfinished or its VAT status unstated. These are the pill list's
 * own reason names (`PILL_REASONS.bakeryService` in @croissants/ui).
 */
export const BAKERY_SERVICE_REASONS = [
  'BAKER',
  'KILL_SWITCH',
  'TWO_UNANSWERED',
  'PAYOUT_SETUP',
  'VAT',
] as const;
export type BakeryServiceReason = (typeof BAKERY_SERVICE_REASONS)[number];

/**
 * That word, from the counter's intake and its readiness (K2).
 *
 * It was derived in the browser from its own Europe/Paris clock parts, its own
 * device fold and a Robobaker exception - so it could read OPEN for a counter
 * the server would not send an order to: a kill switch, a house whose payouts
 * are not verified, a closure the row did not carry. The intake is the
 * server's own answer to "can this counter receive an order, and why not"
 * (`counterIntakeOf`, the counter reads it too), and this maps its reasons
 * onto the table's seven words:
 *
 *  - PAUSED: the baker paused or the kill switch reached it - each stops new
 *    orders and none is the calendar;
 *  - SUSPENDED: the house cannot be paid - its payout setup is unfinished or
 *    its VAT status unstated - which its standing already calls Suspended
 *    (#2004, P261: it read Paused here, Suspended on Standing);
 *  - IGNORING_ORDERS: two orders went unanswered (BAK-038);
 *  - INCOMPLETE: the shop lacks what a courier is booked on (BAK-035) - its
 *    own word, because unlike a pause it is a row an operator completes;
 *  - VACATION: a closure covers today; CLOSED: not a trading hour;
 *  - otherwise - open, or open with this hour full - the counter's readiness
 *    decides: a device that can receive an order is OPEN, else UNREACHABLE.
 *    A demo Robobaker reads READY on its own (tablet-health.ts).
 */
export function bakeryServiceStatusOf(
  intake: Pick<CounterIntake, 'reason'>,
  readiness: CounterReadiness,
): BakeryServiceStatus {
  switch (intake.reason) {
    case 'PLATFORM_SWITCH':
    case 'PAUSED':
    case 'UNATTENDED_HEARTBEAT':
      return 'PAUSED';
    case 'PAYOUT_UNVERIFIED':
      return 'SUSPENDED';
    case 'UNATTENDED_EXPIRED':
      return 'IGNORING_ORDERS';
    case 'PICKUP_INCOMPLETE':
      return 'INCOMPLETE';
    case 'CLOSURE_DAYS':
      return 'VACATION';
    case 'WEEKLY_CLOSURE':
    case 'OUTSIDE_HOURS':
      return 'CLOSED';
    case 'CAPACITY_FULL':
    case 'OPEN':
      return isCounterDispatchable(readiness) ? 'OPEN' : 'UNREACHABLE';
  }
}

/**
 * The reason beside that word, where the word alone does not say (#2004):
 * who paused the counter, or what keeps its house from being paid. Null for
 * every other word - an open, closed or unreachable counter's reason (its
 * closure's end, its devices) is read off the row's own fields, and BAK-038's
 * retired silence rule is never sent.
 *
 * `vatStated` is the house's VAT status being on record: a house that cannot
 * be paid is named for its VAT when that is missing, and for its payout setup
 * otherwise (`payoutVerified` in `counterIntakeOf` is both at once).
 */
export function bakeryServiceReasonOf(
  intake: Pick<CounterIntake, 'reason'>,
  house: { readonly vatStated: boolean },
): BakeryServiceReason | null {
  switch (intake.reason) {
    case 'PAUSED':
      return 'BAKER';
    case 'PLATFORM_SWITCH':
      return 'KILL_SWITCH';
    case 'UNATTENDED_EXPIRED':
      return 'TWO_UNANSWERED';
    case 'PAYOUT_UNVERIFIED':
      return house.vatStated ? 'PAYOUT_SETUP' : 'VAT';
    case 'UNATTENDED_HEARTBEAT':
    case 'PICKUP_INCOMPLETE':
    case 'CLOSURE_DAYS':
    case 'WEEKLY_CLOSURE':
    case 'OUTSIDE_HOURS':
    case 'CAPACITY_FULL':
    case 'OPEN':
      return null;
  }
}
